import { ApiClient } from '@/api/client';
import { ShopJsonldShopLocatorRead } from '@/api/generated';
import { useQuery } from '@tanstack/react-query';
import { useModal } from 'context/Modal/Context';
import { MODAL_NAME } from 'context/Modal/MODAL_NAME';
import useApi from 'hooks/useApi';
import { useTranslation } from 'react-i18next';
import { apiDistanceUnit } from 'utils/distances';
import shopsFactory from 'utils/factories/shop';
import { useDecodeFilters } from 'utils/filters/encodeDecodeFilters';

type ShopLocatorFilter = Partial<Parameters<ApiClient['shops']['apiShopslocatorGetCollection']>[0]>;

const useStoresLocator = () => {
  const { countryCode, latitude, longitude, displayMap, ...currFilters } = useDecodeFilters();
  const apiClient = useApi();
  const { i18n } = useTranslation();

  const filters: ShopLocatorFilter = {
    ...currFilters,
    country: (countryCode as string) || undefined,
    latitude: Number(latitude) ?? undefined,
    longitude: Number(longitude) ?? undefined,
    unit: apiDistanceUnit(i18n.language),
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: shopsFactory.list(filters),
    queryFn: async () => apiClient.shops.apiShopslocatorGetCollection(filters),
    enabled: !!(filters.latitude && filters.longitude),
    select: (v) => v.data,
  });
  const { openModal } = useModal();

  const handleClickShop = (shop: ShopJsonldShopLocatorRead) => {
    openModal(MODAL_NAME.SHOP_DETAILS, { shopId: shop.id! });
  };

  return {
    data: data?.['hydra:member'],
    totalItems: data?.['hydra:totalItems'],
    isLoading,
    isFetching,
    handleClickShop,
  };
};

export default useStoresLocator;
